import dayjs from 'dayjs';
import * as Yup from 'yup';

export const emailVailationSchema = Yup.object().shape({
  email: Yup.string().email('council.create.errorEmail'),
});

export const sendRemindCouncilValidationSchema = Yup.object().shape({
  title: Yup.string().required('formControl.validation.required'),
  content: Yup.string().required('formControl.validation.required').nullable(),
});

export const updateCouncilValidationSchema = Yup.object().shape({
  name: Yup.string().required('formControl.validation.required'),
  establishDate: Yup.string().required('formControl.validation.required'),
  decisionToEstablish: Yup.mixed().when('decisionToEstablishFileUrl', {
    is: (value) => !value,
    then: Yup.mixed().test('isRequired', 'formControl.validation.inputFile', (file) => {
      if (file) return true;
      return false;
    }),
  }),
  membersFields: Yup.array().required('formControl.validation.required'),
  reviewDeadline: Yup.mixed()
    .required('formControl.validation.required')
    .nullable()
    .test(
      'isLargerOrEqualEstablishDate',
      'formControl.validation.inputReviewDeadlineCompareEstablishDateCouncil',
      (value, testContext) => {
        if (!value) return true;

        const establishTimeDate = new Date(testContext.parent.establishDate);
        const scheduledMeetingTimeDate = new Date(value);
        if (establishTimeDate <= scheduledMeetingTimeDate) return true;

        return false;
      }
    ),
});

export const updateCouncilResultValidationSchema = Yup.object().shape({
  meetingDate: Yup.mixed()
    .test(
      'isLargerOrEqualEstablishDate',
      'formControl.validation.inputMeetingDateCompareEstablishDateDateCouncil',
      (value, testContext) => {
        if (!value) return true;

        if (testContext.parent?.establishDate) {
          const establishTimeDate = dayjs(testContext.parent.establishDate).startOf('date');

          if (dayjs(value).startOf('date').isBefore(establishTimeDate)) return false;

          return true;
        }

        return true;
      }
    )
    .test(
      'isLargerOrEqualreviewDeadline',
      'formControl.validation.inputMeetingDateCompareReviewDeadlineCouncil',
      (value, testContext) => {
        if (!value || !testContext.parent?.reviewDeadline) return true;
        const meetingTimeDate = new Date(value);
        const reviewDeadlineDate = new Date(testContext.parent.reviewDeadline);
        if (meetingTimeDate >= reviewDeadlineDate) return true;

        return false;
      }
    ),
  averageScore: Yup.number()
    .nullable()
    .min(0, 'formControl.validation.inputAverageScoreCouncil')
    .max(100, 'formControl.validation.inputAverageScoreCouncil'),
  status: Yup.string().required('formControl.validation.required'),
  totalScore: Yup.mixed()
    .nullable()
    .test(
      'checkMaxScoreInCouncil',
      'formControl.validation.checkMaxScoreInCouncil',
      (value, testContext) => {
        if (
          !value ||
          value <= 100 * testContext.parent.numberMembersAllowReview ||
          testContext.parent.numberMembersAllowReview === 0
        )
          return true;

        return false;
      }
    ),
});

export const createCouncilValidationSchema = Yup.object().shape({
  name: Yup.string().required('formControl.validation.required'),
  establishDate: Yup.string().required('formControl.validation.required'),
  membersFields: Yup.array().required('formControl.validation.required'),
  reviewDeadline: Yup.mixed()
    .required('formControl.validation.required')
    .nullable()
    .test(
      'isLargerOrEqualEstablishDate',
      'formControl.validation.inputReviewDeadlineCompareEstablishDateCouncil',
      (value, testContext) => {
        if (!value) return true;

        const establishTimeDate = new Date(testContext.parent.establishDate);
        const reviewDeadlineDate = new Date(value);
        if (establishTimeDate <= reviewDeadlineDate) return true;

        return false;
      }
    ),
  decisionToEstablish: Yup.mixed()
    .test('isRequired', 'formControl.validation.inputFile', (file) => {
      if (file) return true;
      return false;
    })
    .nullable(),
});

export const membersValidationSchema = Yup.object({
  email: Yup.string()
    .matches(/^[A-Za-z0-9-+"'@._!#$%&*/=^`{|}]+$|^$/, 'formControl.validation.emailInvalid')
    .email('formControl.validation.emailInvalid'),
  fullname: Yup.string().required('formControl.validation.required'),
  gender: Yup.string().required('formControl.validation.required'),
  academicDegree: Yup.string().required('formControl.validation.required'),
  contact: Yup.lazy((value) => {
    if (typeof value === 'object') {
      return Yup.object({
        id: Yup.string().required('formControl.validation.required'),
        fullname: Yup.string('formControl.validation.required'),
      })
        .required('formControl.validation.required')
        .nullable();
    }
    if (typeof value === 'string') {
      return Yup.string()
        .matches(/^[A-Za-z0-9-+"'@._!#$%&*/=^`{|}]+$|^$/, 'formControl.validation.emailInvalid')
        .email('formControl.validation.emailInvalid');
    }
    return Yup.mixed();
  }),

  organizationId: Yup.string().required('formControl.validation.required'),
  organizationEnglishName: Yup.string().nullable(),
  specificOrganizationName: Yup.string()
    .nullable()
    .when('organizationEnglishName', {
      is: 'Other',
      then: Yup.string().required('formControl.validation.required'),
    }),
  roleId: Yup.string().required('formControl.validation.required'),
});

export const createReviewValidationSchema = Yup.object().shape({
  conclusion: Yup.string().required('formControl.validation.required').nullable(),
  score: Yup.number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .min(0, 'formControl.validation.inputAverageScoreCouncil')
    .max(100, 'formControl.validation.inputAverageScoreCouncil')
    .required('formControl.validation.required')
    .nullable(),
  reportFile: Yup.mixed().test('isRequired', 'formControl.validation.inputFile', (value) => {
    if (value) return true;
    return false;
  }),
});

export const updateReviewValidationSchema = Yup.object().shape({
  conclusion: Yup.string().required('formControl.validation.required').nullable(),
  score: Yup.number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .min(0, 'formControl.validation.inputAverageScoreCouncil')
    .max(100, 'formControl.validation.inputAverageScoreCouncil')
    .required('formControl.validation.required')
    .nullable(),
  reportFile: Yup.mixed().nullable(),
});

export const updateExplanationRequestValidationSchema = Yup.object().shape({
  status: Yup.string().oneOf(['NOT_REVIEWED', 'NOT_QUALIFIED', 'QUALIFIED']).notRequired(),
  isOpened: Yup.boolean().oneOf([true, false]).required(),
  comment: Yup.string().required('projectProposal.requestUpdate.validationErrors.commentRequired'),
});

export const createOrUpdatePrequalificationCouncilSchema = Yup.object().shape({
  councilStatus: Yup.string().required('formControl.validation.required'),
});

export const createCouncilSchema = Yup.object().shape({
  ...createCouncilValidationSchema.fields,
  councilTypeId: Yup.string().required('formControl.validation.required'),
});
